import { FC } from 'react';
import {
  Box,
  withStyles,
  Card,
  Flag,
  formatAmount,
  Tooltip,
  MaterialSymbol,
  InterestAccount,
  InterestAccountRatesResponse,
  InterestTerm,
  InterestRate,
} from 'vopay';
import { Title, Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { AccountType, TransferType } from '../AccountSummaryPage';
import { Balance } from '@/hooks/useApi/useAccountBalances';
import ActionsMenu from '../AccountUI/ActionsMenu';
// import StartInterestTooltip from '../AccountUI/Tooltip/StartInterestTooltip';
import AnnualRatesTooltip from '../AccountUI/Tooltip/AnnualRatesTooltip';
import styles from './BalanceDetails.module.scss';

const cn = withStyles(styles);

type BalanceDetailsProps = {
  accountType: AccountType;
  balance: Balance;
  shortTermAccount: InterestAccount;
  longTermAccount: InterestAccount | null;
  interestRatesResponse: InterestAccountRatesResponse;
  onOpenUpgradePlanModal: () => void;
  onOpenFundTransferModal: (type: TransferType) => void;
};

const BalanceDetails: FC<BalanceDetailsProps> = ({
  accountType,
  balance,
  shortTermAccount,
  longTermAccount,
  interestRatesResponse,
  onOpenUpgradePlanModal,
  onOpenFundTransferModal,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });

  const interestTerms: InterestTerm[] = ['short-term', 'long-term'];
  // const negativeBalanceInterestRate = interestRatesResponse.InterestRates.find(
  //   (rate) => rate.Term === 'short-term' && !rate.RangeStart,
  // )?.AnnualInterestRate;

  const [shortTermInterest, longTermInterest] = interestTerms.map((term) =>
    getInterestRate(
      term,
      parseFloat(term === 'short-term' ? shortTermAccount.EligibleBalance : longTermAccount?.EligibleBalance || ''),
      interestRatesResponse.InterestRates,
    ),
  );

  return (
    <div className={cn('balanceDetailsContainer')}>
      <Card
        className={cn('header')}
        style={{
          padding: '24px',
        }}
      >
        <Flag flag={balance.Currency} width="32px" />
        <Title level="4">{balance.Currency}</Title>
      </Card>

      <Card style={{ padding: '10px 12px' }}>
        <div className={cn('row')} style={{ padding: '6.5px 8px' }}>
          <Box width="100%" jc="sb" ai="center">
            <Box>
              {accountType === 'disabled' ? (
                <Title level="4" emphasized>
                  {t('details', { defaultValue: 'Details' })}
                </Title>
              ) : (
                <Title level="5">{t('standard', { defaultValue: 'Standard' })}</Title>
              )}

              {/* {accountType === 'disabled' && (
                <StartInterestTooltip
                  onOpenModal={onOpenUpgradePlanModal}
                  customElement={
                    <div className={cn('tierLevel', 'tierLevelDisabled')}>
                      <div className={cn('circleContainer')}>
                        {Array(4)
                          .fill(undefined)
                          .map((_, index) => (
                            <div key={index} className={cn('circle', 'disabledInterest')} />
                          ))}
                      </div>
                      <div className={cn('interest')}>0%</div>
                    </div>
                  }
                />
              )}*/}

              {accountType !== 'disabled' && (
                <AnnualRatesTooltip
                  accountType="standard"
                  balance={Number(shortTermAccount.EligibleBalance)}
                  interestRates={interestRatesResponse.InterestRates}
                  customElement={
                    <div className={cn('tierLevel', 'tierLevelShortTerm')}>
                      <div className={cn('circleContainer')}>
                        {Array.from({ length: shortTermInterest.tiers }, (_, index) => (
                          <div
                            key={index}
                            className={cn(
                              'circle',
                              index < shortTermInterest.tierLevel ? 'shortTermActive' : 'shortTerm',
                            )}
                          />
                        ))}
                      </div>
                      <div className={cn('interest', 'shortTermInterest')}>{shortTermInterest.interestRate}%</div>
                    </div>
                  }
                />
              )}
            </Box>
            <Box ai="center">
              {/* {accountType === 'disabled' && balance.Currency === 'CAD' && (
                <Button colorScheme="primary" size="small" onClick={onOpenUpgradePlanModal}>
                  {t('upgradeButton')}
                </Button>
              )}*/}

              {accountType === 'standard' && balance.Currency === 'CAD' && (
                <Button
                  colorScheme="feature"
                  size="small"
                  iconRightProps={{ icon: 'auto_awesome', color: '#fbfbfb', fillAmount: 1 }}
                  onClick={onOpenUpgradePlanModal}
                >
                  {t('enableButton')}
                </Button>
              )}

              <span className={cn('actions')}>
                <ActionsMenu
                  isPremium={false}
                  currency={balance.Currency}
                  onOpenFundTransferModal={onOpenFundTransferModal}
                />
              </span>
            </Box>
          </Box>
        </div>

        <div className={cn('row')}>
          <span className={cn('key')}>{t('accountBalance')}</span>
          <span className={cn(balance.AccountBalance < 0 && 'negative')}>
            {balance.Currency} {formatAmount(balance.AccountBalance)}
          </span>
        </div>

        <div className={cn('row')}>
          <span className={cn('key')}>{t('pendingBalance')}</span>
          <span className={cn(balance.PendingFunds < 0 && 'negative')}>
            {balance.Currency} {formatAmount(balance.PendingFunds)}
          </span>
        </div>

        <div className={cn('row')}>
          <span className={cn('key')}>{t('reserveAmount')}</span>
          <span className={cn(balance.Reserve < 0 && 'negative')}>
            {balance.Currency} {formatAmount(balance.Reserve)}
          </span>
        </div>

        <div className={cn('row')}>
          <Box gapSize="4px" ai="center">
            <span className={cn('key')}>{t('rollingReserveNsf')}</span>
            <Tooltip
              position="right"
              icon="info"
              information={
                <Box gapSize="8px" flx="col">
                  <span className={cn('tooltipTitle')}>{t('rollingReserveNsf')}</span>
                  <p className={cn('message')}>{t('rollingReserveNsfInfo')}</p>
                </Box>
              }
            />
          </Box>

          <span className={cn(balance.RollingReserveNSF < 0 && 'negative')}>
            {balance.Currency} {formatAmount(balance.RollingReserveNSF)}
          </span>
        </div>

        <div className={cn('row')}>
          <Box gapSize="4px" ai="center">
            <span className={cn('key')}>{t('rollingReserveReturns')}</span>
            <Tooltip
              position="right"
              icon="info"
              information={
                <Box gapSize="8px" flx="col">
                  <span className={cn('tooltipTitle')}>{t('rollingReserveReturns')}</span>
                  <p className={cn('message')}>{t('rollingReserveReturnsInfo')}</p>
                </Box>
              }
            />
          </Box>

          <span className={cn(balance.RollingReserveReturns < 0 && 'negative')}>
            {balance.Currency} {formatAmount(balance.RollingReserveReturns)}
          </span>
        </div>

        <div className={cn('row')}>
          <Box gapSize="4px" ai="center">
            <span className={cn('key')}>{t('availableBalance')}</span>
            <Tooltip
              position="right"
              icon="info"
              information={
                <Box gapSize="8px" flx="col">
                  <span className={cn('tooltipTitle')}>{t('availableBalance')}</span>
                  <p className={cn('message')}>{t('availableBalanceInfo')}</p>
                </Box>
              }
            />
          </Box>

          <span className={cn(balance.AvailableFunds < 0 && 'negative')}>
            {balance.Currency} {formatAmount(balance.AvailableFunds)}
          </span>
        </div>

        {accountType !== 'disabled' && (
          <div
            className={cn(
              'interestDetailsContainer',
              // accountType === 'disabled' && 'disabled'
            )}
          >
            <div className={cn('title')}>
              <Title level="6">{t('interestDetails')}</Title>
            </div>

            <div className={cn('row')}>
              <Box gapSize="4px" ai="center">
                <span className={cn('key')}>{t('eligibleBalance')}</span>
                <Tooltip
                  position="right"
                  icon="info"
                  information={<span className={cn('message')}>{t('eligibleBalanceTooltip')}</span>}
                />
              </Box>
              <span>
                {balance.Currency}
                {/* {accountType === 'disabled' ? '0.00' : formatAmount(shortTermAccount.EligibleBalance)} */}{' '}
                {formatAmount(shortTermAccount.EligibleBalance)}
              </span>
            </div>

            <div className={cn('row')}>
              <span className={cn('key')}>{t('interestEarned')}</span>
              <span>
                {balance.Currency}
                {/* {accountType === 'disabled' ? '0.00' : formatAmount(shortTermAccount.InterestAccrued) */}{' '}
                {formatAmount(shortTermAccount.InterestAccrued)}
              </span>
            </div>

            <div className={cn('row')}>
              <span className={cn('key')}>{t('interestRate')}</span>
              {/* <span>{accountType === 'disabled' ? '0.00' : formatAmount(shortTermInterest.interestRate)}%</span> */}
              <span>{formatAmount(shortTermInterest.interestRate)}%</span>
            </div>
          </div>
        )}
      </Card>

      {/* It will be added back later on */}
      {/* <Box ai="center" gapSize="5px">
        <div className={cn('interestInfo')}>{t('interestInfo')}</div>
        <Tooltip
          position="bottom"
          icon="info"
          information={
            <Box gapSize="8px" flx="col">
              <Title level="6">{t('overdraftTitle')}</Title>
              <p className={cn('message')}>{t('overdraftInfo', { interest: negativeBalanceInterestRate })}</p>
            </Box>
          }
        />
      </Box> */}

      {accountType === 'premium' && longTermAccount && (
        <Card
          style={{
            padding: '10px 12px',
            borderColor: 'linear-gradient(135deg, hsla(216, 100%, 59%, 0.2), hsla(216, 100%, 59%, 0.8))',
            borderWidth: 2,
            dropShadow: '0 0 34px hsla(216, 100%, 59%, 0.15)',
          }}
        >
          <div className={cn('row')}>
            <Box width="100%" jc="sb" ai="center">
              <Box>
                <Title level="5">
                  <span className={cn('premiumTitle')}>{t('premium', { defaultValue: 'Premium' })}</span>
                </Title>

                <AnnualRatesTooltip
                  accountType="premium"
                  balance={Number(longTermAccount.EligibleBalance)}
                  interestRates={interestRatesResponse.InterestRates}
                  customElement={
                    <div className={cn('tierLevel', 'tierLevelLongTerm')}>
                      <div className={cn('circleContainer')}>
                        {Array.from({ length: longTermInterest.tiers }, (_, index) => (
                          <div
                            key={index}
                            className={cn('circle', index < longTermInterest.tierLevel ? 'longTermActive' : 'longTerm')}
                          />
                        ))}
                      </div>
                      <div className={cn('interest', 'longTermInterest')}>{longTermInterest.interestRate}%</div>
                      <MaterialSymbol icon="auto_graph" color="#fbfbfb" />
                    </div>
                  }
                />
              </Box>

              <Box ai="center">
                <span className={cn('actions')}>
                  <ActionsMenu
                    isPremium
                    currency={balance.Currency}
                    onOpenFundTransferModal={onOpenFundTransferModal}
                  />
                </span>
              </Box>
            </Box>
          </div>

          <div className={cn('row')}>
            <span className={cn('key')}>{t('eligibleBalance')}</span>
            <span>
              {balance.Currency} {formatAmount(longTermAccount.EligibleBalance)}
            </span>
          </div>

          <div className={cn('row')}>
            <span className={cn('key')}>{t('interestEarned')}</span>
            <span>
              {balance.Currency} {formatAmount(longTermAccount.InterestAccrued)}
            </span>
          </div>

          <div className={cn('row')}>
            <span className={cn('key')}>{t('interestRate')}</span>
            <span>{formatAmount(longTermInterest.interestRate)}%</span>
          </div>
        </Card>
      )}
    </div>
  );

  function getInterestRate(
    term: InterestTerm,
    balance: number,
    rates: InterestRate[],
  ): { interestRate: string; tierLevel: number; tiers: number } {
    const applicableRates = rates.filter((rate) => rate.Term === term);

    if (applicableRates.length === 0) {
      // If no applicable rates are found, return 0
      return {
        interestRate: '0.00',
        tierLevel: 0,
        tiers: 4,
      };
    }

    const matchingRate = applicableRates.find(
      (rate) => balance >= parseFloat(rate.RangeStart) && balance <= parseFloat(rate.RangeEnd),
    );

    if (!matchingRate) {
      // Check if balance exceeds the last tier's range
      const lastRate = applicableRates[applicableRates.length - 1];
      if (balance > parseFloat(lastRate.RangeEnd)) {
        return {
          interestRate: lastRate?.AnnualInterestRate ?? '0.00',
          tierLevel: applicableRates.length,
          tiers: applicableRates.length,
        };
      }
      // If balance is below the first tier's range, return 0
      return {
        interestRate: '0.00',
        tierLevel: 0,
        tiers: applicableRates.length,
      };
    }

    const tierLevel = applicableRates.indexOf(matchingRate);

    return {
      interestRate: matchingRate?.AnnualInterestRate ?? '0.00',
      tierLevel: tierLevel !== -1 ? tierLevel + 1 : 0, // add 1 to make it 1-based
      tiers: applicableRates.length,
    };
  }
};

export default BalanceDetails;
